/**
 * @file 发送停留时长上报
 * @author dafo<huanghouin@baijiahulian.com>
 * @module module/stay
 */
import request from './util/request';
import configurable from './util/configurable';
import performance from './util/performance';
import {
    on,
    off
} from './util/dom';

import {
    trackID,
    getUserID,
    Config,
    ROUTE_MODE,
    documentHidden,
    documentVisibilityChange,
    getInnerEventName
} from './util/config';
import warnForbiddenKeys from './util/forbiddenWords';
import metric from './metric';

import onRouteChange from './util/onRouteChange';
import getPath from './util/getPath';


const win = typeof window !== 'undefined' && window;
const doc = win && document;

// 当前浏览器支持的 hidden 和 visibilityChange 写法
const [hidden, visibilityChange] = [
    ['hidden', 'visibilitychange'],
    ['webkitHidden', 'webkitvisibilitychange'],
    ['mozHidden', 'mozvisibilitychange'],
    ['msHidden', 'msvisibilitychange']
].filter(([key]) => key in (doc || {}))[0] || [];

// 进入页面时间戳
let start = performance.timing && performance.timing.responseEnd || +new Date();

// 跳出时间
let bounceTimes = 0;

// 活动时间
let activeTime = 0;

// 最后活动时间
let lastActiveTime = start;

// 切换路由，销毁监听
let teardown;

/**
 * 上一个页面的 URL，对于 SPA 来说是上一个路由的地址
 */
let previousPageURL = '';


/**
 * 上一个页面的 href
 */
let previousPageHref = '';

// 停留时长统计
const changeCount = () => {
    const now = +new Date();

    if (doc[documentHidden]) {
        if (bounceTimes) {
            activeTime += now - lastActiveTime;
        }
        else {
            activeTime = now - start;
        }

        bounceTimes++;
    }
    else {
        lastActiveTime = now;
    }
};

on(doc, documentVisibilityChange, changeCount);

/**
  * 时长上报
  *
  * @function
  * @param {Object} json 要上报的数据
  * @param {Function=} callback 上报后的回调
  */
const report = configurable((json, callback) => {
    const now = +new Date();

    // 三个重要指标，避免覆盖真实统计值（作弊）
    /* eslint-disable no-unused-vars */
    const {
        at,
        bt,
        tt,
        ...rest
    } = json;
    /* eslint-enable no-unused-vars */

    // 要上报的数据
    const stayData = {
        // 当前页面 URL
        url: previousPageHref,

        // 用户的唯一标识，由后端生成写到 cookie 中
        uuid: trackID,

        // 登录用户 ID，用户登录后端写到 cookie 中
        uid: getUserID(),

        // 页面路径归类
        path: previousPageURL,

        // Active Time 活动时长
        at: activeTime + (now - lastActiveTime),

        // Bounce Times 切换次数
        bt: bounceTimes,

        // Total Time 总停留时长
        tt: now - start,

        ...rest
    };

    // 上报后重置相关统计
    activeTime = 0;
    bounceTimes = 0;
    start = lastActiveTime = now;
    previousPageURL = getPath();
    previousPageHref = location.href;

    warnForbiddenKeys(stayData);
    // 为保证上报成功和简化逻辑，只支持 POST 方式的上报
    request('post', true)(`${Config.STAY}`, stayData, callback);

    // 自动上报点击指标数据到轻舟
    metric({
        ...stayData,
        metric: 'stay'
    });

}, {});

function observe() {
    report({});
}

/**
  * 解除相关事件的绑定
  */
function destroy() {
    off(doc, visibilityChange, changeCount);
    off(doc, documentVisibilityChange, changeCount);

    off(win, getInnerEventName('pushstate'), observe);
    off(win, getInnerEventName('replacestate'), observe);
    off(win, 'popstate', observe);
    off(win, 'hashchange', observe);
    off(win, 'beforeunload', observe);
    off(win, 'unload', destroy);
    typeof teardown === 'function' && teardown();
}

report.init = number => {
    if (Object.values(ROUTE_MODE).includes(+number)) {
        Config.ROUTE_MODE = +number;
    }
    previousPageURL = getPath();
    previousPageHref = location.href;

    teardown = onRouteChange(e => {
        observe();
    });
};

on(win, 'beforeunload', observe);
on(win, 'unload', destroy);

export default report;
